import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { clinicalActionsAPI } from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Textarea } from '../../components/ui/textarea';
import { 
  Plus, 
  Pencil, 
  Trash, 
  FirstAid,
  MagnifyingGlass,
  Spinner,
  WarningCircle,
  Clock,
  Power
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import EffectsEditor from '../../components/effects/EffectsEditor';

const CATEGORIES = [
  'Vía Aérea',
  'Circulación',
  'Monitorización',
  'Diagnóstico',
  'Procedimiento',
  'Evaluación',
  'Soporte Vital',
  'Otro'
];

const ACTION_TYPES = [
  { value: 'procedimiento', label: 'Procedimiento' },
  { value: 'evaluacion', label: 'Evaluación' },
  { value: 'intervencion', label: 'Intervención' }
];

const defaultAction = {
  nombre: '',
  categoria: 'Otro',
  tipo: 'procedimiento',
  descripcion: '',
  tiempo_estimado: 30,
  impacto_clinico: '',
  efectos_vitales: {},
  duracion_segundos: 30
};

export default function ClinicalActionsPage() {
  const { user, isSuperAdmin, hasPermission } = useAuth();
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showInactive, setShowInactive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [formData, setFormData] = useState(defaultAction);
  const [saving, setSaving] = useState(false);

  const canCreate = isSuperAdmin || hasPermission('acciones', 'crear');
  const canEdit = isSuperAdmin || hasPermission('acciones', 'editar');
  const canToggle = isSuperAdmin || hasPermission('acciones', 'activar_desactivar');

  useEffect(() => {
    loadActions();
  }, [showInactive]);

  const loadActions = async () => {
    try {
      const response = await clinicalActionsAPI.getAll(showInactive);
      setActions(response.data);
    } catch (error) {
      toast.error('Error al cargar acciones clínicas');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (action = null) => {
    if (action) {
      setEditingAction(action);
      setFormData({ ...defaultAction, ...action });
    } else {
      setEditingAction(null);
      setFormData(defaultAction);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAction(null);
    setFormData(defaultAction);
  };

  const handleSave = async () => {
    if (!formData.nombre) {
      toast.error('Complete el nombre de la acción');
      return;
    }

    setSaving(true);
    try {
      if (editingAction) {
        await clinicalActionsAPI.update(editingAction.id, formData);
        toast.success('Acción actualizada');
      } else {
        await clinicalActionsAPI.create(formData);
        toast.success('Acción creada');
      }
      handleCloseModal();
      loadActions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (action) => {
    if (!window.confirm(`¿Eliminar ${action.nombre}?`)) return;

    try {
      await clinicalActionsAPI.delete(action.id);
      toast.success('Acción eliminada');
      loadActions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleToggleActive = async (action) => {
    try {
      await clinicalActionsAPI.toggleActive(action.id);
      toast.success(action.is_active !== false ? 'Acción desactivada' : 'Acción activada');
      loadActions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const filteredActions = actions.filter(action => {
    const matchesSearch = (action.nombre || '').toLowerCase().includes((searchTerm || '').toLowerCase()) ||
                         (action.categoria || '').toLowerCase().includes((searchTerm || '').toLowerCase());
    const matchesCategory = selectedCategory === 'all' || action.categoria === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Group by category
  const groupedActions = filteredActions.reduce((acc, action) => {
    const cat = action.categoria || 'Otro';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(action);
    return acc;
  }, {});

  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center h-64">
          <Spinner size={48} className="animate-spin text-blue-600" />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold font-['Chivo'] text-slate-900" data-testid="actions-title">
              Acciones Clínicas
            </h1>
            <p className="text-slate-500">
              {actions.length} acciones registradas
            </p>
          </div>
          <div className="flex items-center gap-3">
            {canToggle && (
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  className="rounded border-slate-300"
                />
                Mostrar inactivas
              </label>
            )}
            <Button variant="outline" className="gap-2" data-testid="bulk-import-link-actions"
              onClick={() => window.location.assign('/app/admin/bulk-import?module=actions')}>
              Carga Masiva
            </Button>
            {canCreate && (
              <Button onClick={() => handleOpenModal()} className="gap-2" data-testid="add-action-btn">
                <Plus size={18} />
                Agregar Acción
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Buscar acciones..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full md:w-48" data-testid="category-filter">
                  <SelectValue placeholder="Categoría" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las categorías</SelectItem>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Actions by Category */}
        {Object.entries(groupedActions).map(([category, categoryActions]) => (
          <div key={category} className="space-y-3">
            <h2 className="text-lg font-semibold text-slate-700 flex items-center gap-2">
              <FirstAid size={20} className="text-blue-500" />
              {category}
              <Badge variant="secondary">{categoryActions.length}</Badge>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categoryActions.map(action => (
                <Card key={action.id} className={`hover:shadow-md transition-shadow ${action.is_active === false ? 'opacity-60' : ''}`} data-testid={`action-card-${action.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base">{action.nombre}</CardTitle>
                        {action.is_active === false && (
                          <Badge className="bg-slate-100 text-slate-600 mt-1">Inactiva</Badge>
                        )}
                      </div>
                      {!action.is_default && (
                        <div className="flex gap-1">
                          {canEdit && (
                            <Button variant="ghost" size="sm" onClick={() => handleOpenModal(action)} data-testid={`edit-${action.id}`}>
                              <Pencil size={16} />
                            </Button>
                          )}
                          {canToggle && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className={action.is_active !== false ? 'text-orange-500 hover:text-orange-700' : 'text-green-500 hover:text-green-700'}
                              onClick={() => handleToggleActive(action)}
                              title={action.is_active !== false ? 'Desactivar' : 'Activar'}
                            >
                              <Power size={16} />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-slate-500">
                      <Clock size={14} />
                      <span>{action.duracion_segundos || 30}s</span>
                      <Badge variant="outline" className="text-xs capitalize">
                        {action.tipo}
                      </Badge>
                    </div>
                    {action.descripcion && (
                      <p className="text-slate-600 text-xs line-clamp-2">{action.descripcion}</p>
                    )}
                    {action.is_default && (
                      <Badge variant="secondary" className="bg-slate-100 text-slate-600">Sistema</Badge>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}

        {Object.keys(groupedActions).length === 0 && (
          <Card className="p-8 text-center">
            <WarningCircle size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No se encontraron acciones clínicas</p>
          </Card>
        )}

        {/* Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingAction ? 'Editar Acción' : 'Nueva Acción Clínica'}</DialogTitle>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh] pr-4">
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="nombre">Nombre *</Label>
                  <Input
                    id="nombre"
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    placeholder="Ej: Intubación orotraqueal"
                    data-testid="action-name-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Categoría</Label>
                    <Select 
                      value={formData.categoria} 
                      onValueChange={(value) => setFormData({ ...formData, categoria: value })}
                    >
                      <SelectTrigger data-testid="action-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Tipo</Label>
                    <Select 
                      value={formData.tipo} 
                      onValueChange={(value) => setFormData({ ...formData, tipo: value })}
                    >
                      <SelectTrigger data-testid="action-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ACTION_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="duracion">Duración (segundos)</Label>
                  <Input
                    id="duracion"
                    type="number"
                    min="1"
                    value={formData.duracion_segundos}
                    onChange={(e) => setFormData({ ...formData, duracion_segundos: parseInt(e.target.value) || 30 })}
                    data-testid="action-duration-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="descripcion">Descripción</Label>
                  <Textarea
                    id="descripcion"
                    value={formData.descripcion || ''}
                    onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                    placeholder="Descripción del procedimiento..."
                    data-testid="action-description-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="impacto">Impacto Clínico</Label>
                  <Textarea
                    id="impacto"
                    value={formData.impacto_clinico || ''}
                    onChange={(e) => setFormData({ ...formData, impacto_clinico: e.target.value })}
                    placeholder="Efectos esperados en el paciente..."
                    data-testid="action-impact-input"
                  />
                </div>

                {/* Physiological Effects Editor — Fase 2 (solo al editar) */}
                {editingAction && (
                  <div className="pt-4 mt-2 border-t border-slate-200">
                    <EffectsEditor
                      sourceType="action"
                      sourceId={editingAction.id}
                      sourceName={editingAction.nombre}
                      disabled={!canEdit}
                    />
                  </div>
                )}
              </div>
            </ScrollArea>
            <DialogFooter>
              <Button variant="outline" onClick={handleCloseModal}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-action-btn">
                {saving ? <Spinner size={18} className="animate-spin" /> : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
